import React from 'react'
import { cn } from './cn'
import { mergeAriaDescribedBy, mergeAriaLabelledBy, useFieldA11y } from './field'

export interface SelectOption {
  value: string
  label: React.ReactNode
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Convenience option list. When provided, renders `<option>`s from it. */
  options?: SelectOption[]
  /** Convenience callback with just the selected value (i18n/state at caller). */
  onValueChange?: (value: string) => void
  /** Fill the width of the caller's constrained control column. */
  fullWidth?: boolean
}

/** Styled native select with a shared, non-interactive chevron. */
export function Select({
  options,
  onValueChange,
  onChange,
  className,
  children,
  fullWidth,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-labelledby': ariaLabelledBy,
  ...rest
}: SelectProps) {
  const generatedId = React.useId()
  const { controlId, invalid: fieldInvalid, labelId, descriptionIds } = useFieldA11y()

  return (
    <select
      id={id ?? controlId ?? generatedId}
      className={cn('input', fullWidth && 'input-full', className)}
      aria-invalid={ariaInvalid ?? (fieldInvalid || undefined)}
      aria-describedby={mergeAriaDescribedBy(ariaDescribedBy, descriptionIds)}
      aria-labelledby={mergeAriaLabelledBy(ariaLabelledBy, labelId)}
      onChange={(event) => {
        onChange?.(event)
        onValueChange?.(event.target.value)
      }}
      {...rest}
    >
      {options
        ? options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))
        : children}
    </select>
  )
}
